/**
 * NFs e a fila de faturamento (spec 02 §5.8–§5.10 · spec 03 §5 · tela 4.5 · passo 4.4).
 *
 * ## O sistema NÃO emite nota
 *
 * Quem numera a NFS-e é a prefeitura. No v1 o operador emite fora e traz o número REAL
 * (`EmissorManual`, spec 06 §3 · DS-68), então `numero` **vem no corpo** e é editável —
 * não há sequência do nosso lado que o gere. Com o `GatewayNfse` da fase 12 o campo
 * vira somente-leitura e o número vem do provedor; o que distingue os dois casos no
 * livro é `origemEmissao` (D-86).
 *
 * ## `emissao` é a âncora da DRE
 *
 * A competência é o MÊS DE EMISSÃO (D-13) — nunca a data da venda nem a do recebimento.
 * É por isso que ela é data nominal `YYYY-MM-DD`: competência não tem hora nem fuso.
 */
import { z } from 'zod'
import { dataNominal, uuid } from './comum.js'

export const situacaoDaNf = z.enum([
  'em_emissao',
  'autorizada',
  'rejeitada',
  'cancelada',
  'substituida',
])
export type SituacaoDaNf = z.infer<typeof situacaoDaNf>

/**
 * D-86 — como a nota chegou ao livro.
 *
 * Hoje só `manual` é alcançável. O campo existe desde já para que, quando a fase 12
 * entrar, as duas origens convivam no MESMO livro sem ambiguidade: sem ele, a única
 * forma de saber a procedência seria a data, e data não é procedência.
 */
export const origemDeEmissao = z.enum(['manual', 'automatica'])
export type OrigemDeEmissao = z.infer<typeof origemDeEmissao>

/** Um pedido coberto pela NF, para a coluna "Pedidos" do livro (links para a 4.2). */
export const pedidoDaNf = z.object({
  pedidoId: uuid,
  codigo: z.string(),
  valor: z.string().nullable(),
})
export type PedidoDaNf = z.infer<typeof pedidoDaNf>

/**
 * A COMPOSIÇÃO da nota — um vínculo por recebível coberto.
 *
 * `pedidos` agrega por pedido e serve à coluna do livro; esta lista é a granularidade
 * em que a SUBSTITUIÇÃO opera. A substituta repõe o documento de um fato financeiro já
 * definido: o mesmo conjunto de recebíveis, cada um com a mesma cobertura fiscal. Sem
 * expor a composição, a tela não teria como mostrar o que precisa ser preservado — ela
 * listava as parcelas do PEDIDO e deixava desmarcá-las, que é outra coisa.
 *
 * `cobertura` é `valor + deducao`, e é ela que a substituta tem de reproduzir. A divisão
 * entre os dois pode mudar (800+200 e 900+100 cobrem os mesmos 1.000); o total, não.
 *
 * Os três são anuláveis pela mesma razão que `valor` da nota: perfil sem valores
 * financeiros recebe `null` (DS-263 · AB-092).
 */
export const vinculoDaNf = z.object({
  recebivelId: uuid,
  pedidoId: uuid,
  pedidoCodigo: z.string(),
  numeroParcela: z.number().int().positive(),
  totalParcelas: z.number().int().positive(),
  vencimento: dataNominal,
  valor: z.string().nullable(),
  deducao: z.string().nullable(),
  cobertura: z.string().nullable(),
})
export type VinculoDaNf = z.infer<typeof vinculoDaNf>

export const nf = z.object({
  id: uuid,
  serie: z.string(),
  numero: z.number().int().positive(),
  /** ⚓ Competência da DRE (D-13). */
  emissao: dataNominal,
  valor: z.string().nullable(),
  moeda: z.string(),
  situacao: situacaoDaNf,
  origemEmissao: origemDeEmissao,
  motivoRejeicao: z.string().nullable(),
  motivoCancelamentoNome: z.string().nullable(),
  motivoCancelamento: z.string().nullable(),
  substituidaPorId: uuid.nullable(),
  substituidaPorNumero: z.number().int().nullable(),
  /** Razão social do faturado no dia da emissão — snapshot, nunca o cadastro vivo. */
  faturadoNome: z.string(),
  faturadoDocumento: z.string().nullable(),
  /** Setor congelado na emissão; nulo quando não registrado na nota antiga. */
  setor: z.enum(['Público', 'Privado']).nullable(),
  observacoes: z.string().nullable(),
  pedidos: z.array(pedidoDaNf),
  /** A composição, na granularidade em que a substituta opera. */
  recebiveis: z.array(vinculoDaNf),
})
export type Nf = z.infer<typeof nf>

/**
 * O que a fila devolve — e a unidade dela passou a ser a PARCELA.
 *
 * Era uma linha por PEDIDO, com a data vindo do gatilho operacional (fim de veiculação
 * ou marco de serviço). A campanha decidia quando o pedido aparecia para ser faturado,
 * e o saldo mostrado era o do pedido inteiro: quem ia emitir precisava escolher a
 * parcela num segundo passo, com o número do pedido na cabeça.
 *
 * Agora a linha É a parcela, a data é `faturarEm` (a data programada da nota, 0131; o
 * combinado com o cliente é o vencimento) e o saldo é o dela. Campanha não participa de nada disto.
 */
export const itemDaFila = z.object({
  /** A PARCELA — a chave da linha. */
  recebivelId: uuid,
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  pedidoId: uuid,
  codigo: z.string(),
  clienteNome: z.string().nullable(),
  /** A empresa da parte Cliente, para o nome abrir a ficha dela (D-136, DS-538). */
  clienteEmpresaId: uuid.nullable(),
  setor: z.enum(['Público', 'Privado']).nullable(),
  perfilFaturamentoRotulo: z.string().nullable(),
  /**
   * O prazo de recebimento congelado no pedido, em dias (0131).
   *
   * Informativo: ele só sugeriu o Faturar em de cada parcela. Registrar a nota não toca
   * o vencimento (DS-11, gatilho da 0012); o vencimento é o da agenda do pedido.
   *
   * Nulo é o caminho da substituta, que nasce da linha do LIVRO e não da fila — o pedido
   * sempre tem perfil de prazo (`perfil_prazo_id` é NOT NULL), mas a nota não carrega
   * qual é. Nesse caso a frase sai sem o número, nunca com um inventado (DS-99).
   */
  prazoDias: z.number().int().nullable(),
  /** A referência da agência para o pedido (D-29) — é por ela que a fila é conferida. */
  refExterna: z.string().nullable(),
  /**
   * De onde veio a PROGRAMAÇÃO desta parcela — não de onde veio um gatilho.
   *
   * Substitui `origemGatilho`, e a troca é de significado, não de nome: aquele contava
   * fim de veiculação, marco de serviço ou ciclo; este responde apenas se a data foi
   * programada no pedido ou nasceu de um ciclo de assinatura. `fim_veiculacao` não
   * existe mais como origem financeira, porque campanha deixou de governar a fila.
   */
  origemProgramacao: z.enum(['programado', 'ciclo_assinatura']),
  /** Quando esta parcela deve ser faturada (0131). Substitui `gatilhoEm`. */
  faturarEm: dataNominal,
  /** A fila NÃO prescreve (exceção 2 da spec 05 §3) — o aging só cresce. */
  agingDias: z.number().int(),
  /** O bruto da parcela e o que já está coberto por nota viva — as duas pontas da conta. */
  valorBruto: z.string().nullable(),
  coberturaFiscal: z.string().nullable(),
  saldoAFaturar: z.string().nullable(),
  /** A nota da rejeição, quando a NF anterior voltou (G19). */
  nota: z.string().nullable(),
  /**
   * Quem autorizou faturar esta parcela e quando (D-209), para a parcela de Pedido sujeito que está na fila
   * (só entra com a autorização vigente). `null` no Pedido que não exige; ausente no dublê antigo.
   */
  autorizacaoParaFaturar: z.object({
    autorizadaPorNome: z.string().nullable(),
    autorizadaEm: z.string(),
    faturarEmAutorizado: dataNominal,
  }).nullable().optional(),
})
export type ItemDaFila = z.infer<typeof itemDaFila>

/**
 * Pedido vivo que NÃO tem agenda financeira — a pendência que a fila não pode esconder.
 *
 * Ele não entra na fila, não recebe agenda automática e não ganha data inventada: a
 * programação é um combinado com o cliente, e fabricá-la seria o sistema decidindo
 * sozinho quando cobrar. Mas ele também não pode sumir — um pedido vendido que ninguém
 * consegue faturar é receita parada em silêncio.
 *
 * Então ele aparece ao lado da fila, dizendo o que falta, com o caminho para resolver
 * no lugar certo: a agenda do Pedido.
 */
export const pedidoSemAgenda = z.object({
  pedidoId: uuid,
  codigo: z.string(),
  clienteNome: z.string().nullable(),
  /** A empresa da parte Cliente (D-136, DS-538). */
  clienteEmpresaId: uuid.nullable(),
  valor: z.string().nullable(),
  dataVenda: dataNominal,
})
export type PedidoSemAgenda = z.infer<typeof pedidoSemAgenda>

/**
 * As parcelas que AINDA NÃO chegaram à data de faturar — o outro lado da fila.
 *
 * O selo "A faturar" do pedido lê o saldo sem nota; a fila lê a PARCELA na data (DS-508).
 * Sem este resumo, a 4.5 dizia "Fila zerada" para quem acabara de ver pedidos "A faturar"
 * no Basquete — e não dizia por quê (item 08 do lote de 22/09/2026). Mesmos critérios da
 * `v_fila_por_parcela`, com a data de faturar ainda por vir.
 */
export const aguardandoDataDeFaturar = z.object({
  quantidade: z.number().int().nonnegative(),
  /** A data de faturar mais próxima entre elas; `null` quando não há nenhuma. */
  proxima: dataNominal.nullable(),
  /** O pedido da próxima, para a tela levar até ele. */
  pedidoId: uuid.nullable(),
  codigo: z.string().nullable(),
})
export type AguardandoDataDeFaturar = z.infer<typeof aguardandoDataDeFaturar>

/**
 * Uma parcela PROGRAMADA PARA FATURAR — com saldo sem nota viva e a data de faturar ainda
 * por vir (DS-636). É o outro lado da fila: mesmo critério de parcela viva de pedido vivo,
 * com a data invertida, e uma consulta só, em `billing/public.ts`, que também dá o
 * `aguardando`. Não é "A faturar" (DS-508): a fila é a parcela cuja data já chegou.
 *
 * `valor` e `saldoAFaturar` caem na máscara por NOME (DS-263): nulos para quem não vê
 * valores. Por isso não se chamam `valorBruto` aqui.
 */
export const itemProgramado = z.object({
  recebivelId: uuid,
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  pedidoId: uuid,
  codigo: z.string(),
  refExterna: z.string().nullable(),
  clienteNome: z.string().nullable(),
  /** A empresa da parte Cliente, a mesma da fila (D-136, DS-538). */
  clienteEmpresaId: uuid.nullable(),
  setor: z.enum(['Público', 'Privado']).nullable(),
  perfilFaturamentoRotulo: z.string().nullable(),
  origemProgramacao: z.enum(['programado', 'ciclo_assinatura']),
  faturarEm: dataNominal,
  vencimento: dataNominal,
  /** `faturar_em − fn_hoje()`, pelo relógio do BANCO (DS-172), como o aging da fila; sempre ≥ 1. */
  diasAte: z.number().int().positive(),
  /** O bruto da parcela e o saldo ainda sem nota viva. */
  valor: z.string().nullable(),
  saldoAFaturar: z.string().nullable(),
})
export type ItemProgramado = z.infer<typeof itemProgramado>

/** Um recebível que a NF vai cobrir — o par vem do modal. */
export const recebivelDaNf = z.object({
  recebivelId: uuid,
  valor: z.string(),
})

export const entradaDeNf = z.object({
  /**
   * Os recebíveis cobertos. Agrupada = de N pedidos do MESMO faturado (422 senão);
   * parcial = valor menor que o do recebível, e o saldo segue faturável.
   */
  recebiveis: z.array(recebivelDaNf).min(1, 'Escolha ao menos um recebível.'),
  emissao: dataNominal,
  valor: z.string(),
  serie: z.string().min(1).default('1'),
  /** DS-68: no v1 ele é obrigatório, porque quem numera é a prefeitura. */
  numero: z.number().int().positive(),
  /**
   * A comissão destacada no CORPO da nota (CENP) — e por que ela precisa entrar aqui.
   *
   * A nota sai com o líquido; a parcela guarda o bruto (D-90). Quem cobre a diferença é
   * a dedução: `cobertura = vínculo + dedução`. Sem este campo, o caminho de emissão
   * real não conseguia registrá-la — só a fixture — e a consequência era em cadeia: a
   * parcela nunca alcançava cobertura total, ficava `pendente` para sempre, o pedido
   * nunca saía da fila, e a tela seguia oferecendo o resíduo como saldo faturável,
   * convidando o operador a emitir uma segunda nota que não existe.
   *
   * Nulo é o caso comum: a maioria das notas não tem comissão destacada.
   */
  deducoes: z
    .object({ total: z.string(), referencia: z.string().nullish() })
    .nullish(),
  observacoes: z.string().nullish(),
})
export type EntradaDeNf = z.infer<typeof entradaDeNf>

export const entradaDeCancelamentoDeNf = z.object({
  motivoId: uuid,
  detalhe: z.string().nullish(),
})
export type EntradaDeCancelamentoDeNf = z.infer<typeof entradaDeCancelamentoDeNf>

/** O retorno do clearance. No v1 é o operador que o informa; na fase 12, o gateway. */
export const entradaDeRetornoDeClearance = z.object({
  resultado: z.enum(['autorizada', 'rejeitada']),
  motivo: z.string().nullish(),
})
export type EntradaDeRetornoDeClearance = z.infer<typeof entradaDeRetornoDeClearance>
